'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';


interface LoginValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

const DEMO_CREDS = { email: 'marcus@detailbook.app', password: 'DetailPro2026!' };

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginValues>();

  const onSubmit = async (data: LoginValues) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/login with email + password
    await new Promise((r) => setTimeout(r, 1500));
    if (data.email === DEMO_CREDS.email && data.password === DEMO_CREDS.password) {
      toast.success('Welcome back, Marcus!');
    } else {
      setError('root', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
    }
    setIsLoading(false);
  };

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofill = () => {
    setValue('email', DEMO_CREDS.email);
    setValue('password', DEMO_CREDS.password);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-700 text-foreground">Welcome back</h1>
        <p className="text-muted-foreground text-sm mt-1">Sign in to manage your bookings</p>
      </div>

      {/* Google OAuth */}
      <button className="btn-secondary w-full mb-4 flex items-center gap-3 justify-center">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
          <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </button>

      <div className="flex items-center gap-3 mb-4">
        <hr className="flex-1 border-border" />
        <span className="text-xs text-muted-foreground font-500">or sign in with email</span>
        <hr className="flex-1 border-border" />
      </div>

      {errors.root && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-600 text-sm font-500">{errors.root.message}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div>
          <label className="block text-xs font-600 text-foreground mb-1.5">Email Address</label>
          <input
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
            placeholder="your@email.com"
            className={`input-field ${errors.email ? 'error' : ''}`}
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-600 text-foreground">Password</label>
            <button type="button" className="text-xs text-primary font-600 hover:underline">
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              {...register('password', { required: 'Password is required' })}
              placeholder="••••••••"
              className={`input-field pr-10 ${errors.password ? 'error' : ''}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="rememberMe"
            {...register('rememberMe')}
            className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
          />
          <label htmlFor="rememberMe" className="text-sm text-muted-foreground cursor-pointer">
            Remember me for 30 days
          </label>
        </div>

        <button type="submit" disabled={isLoading} className="btn-primary w-full mt-1">
          {isLoading ? (
            <>
              <Loader2 size={15} className="animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <p className="text-sm text-center text-muted-foreground mt-4">
        Don't have an account?{' '}
        <button onClick={onSwitchToSignup} className="text-primary font-600 hover:underline">
          Create one free
        </button>
      </p>

      {/* Demo Credentials Box */}
      <div className="mt-6 bg-muted rounded-xl p-4 border border-border">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-700 text-foreground uppercase tracking-wider">Demo Credentials</p>
          <button
            onClick={autofill}
            className="text-xs text-primary font-600 hover:underline"
          >
            Autofill
          </button>
        </div>
        <div className="flex flex-col gap-2">
          {[
            { label: 'Email', value: DEMO_CREDS.email, field: 'email' },
            { label: 'Password', value: DEMO_CREDS.password, field: 'password' },
          ].map((item) => (
            <div key={`cred-${item.field}`} className="flex items-center justify-between bg-card rounded-lg px-3 py-2 border border-border">
              <div>
                <p className="text-[10px] font-600 text-muted-foreground uppercase tracking-wider">{item.label}</p>
                <p className="text-xs font-600 text-foreground font-mono">{item.value}</p>
              </div>
              <button
                onClick={() => copyToClipboard(item.value, item.field)}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                aria-label={`Copy ${item.label}`}
              >
                {copiedField === item.field ? (
                  <Check size={13} className="text-emerald-500" />
                ) : (
                  <Copy size={13} />
                )}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}